import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Volume2, 
  Mic, 
  MicOff, 
  Loader2, 
  Send, 
  RefreshCw 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a unique session ID for Manee
const getManeeSessionId = () => {
  let sessionId = localStorage.getItem('ploy_session_id');
  if (!sessionId) {
    sessionId = `ploy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ploy_session_id', sessionId);
  }
  return sessionId;
};

const ManeeChat = ({ isOpen, onClose, context }) => {
  const [messages, setMessages] = useState([
    {
      role: 'manee',
      content: "สวัสดีค่ะ! 😊 Je suis มณี, ta prof de thaï. Pose-moi tes questions sur la langue, la culture, la géographie, ou tout ce qui fait la Thaïlande.\n\n🎤 Tape sur le micro pour me poser ta question à l'oral, et rappuie dessus quand tu as fini. Pour t'entraîner à prononcer, c'est dans le module Entraînement."
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const sessionId = getManeeSessionId();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInputText('');
    setIsLoading(true);

    try {
      // Get beta code from localStorage for logging
      const betaCode = localStorage.getItem('betaCode') || null;
      
      const response = await axios.post(`${API}/ploy/chat`, {
        session_id: sessionId,
        message: text,
        context: context || null,
        beta_code: betaCode
      });

      setMessages(prev => [...prev, { role: 'manee', content: response.data.response }]);
    } catch (err) {
      console.error('Manee chat error:', err);
      // User-friendly error message
      const errorMessage = err.response?.status === 402 || err.response?.data?.detail?.includes('insufficient')
        ? "Le service est temporairement indisponible. Réessaye dans quelques instants."
        : "Oops, j'ai eu un petit problème 😅 Réessaie ta question !";
      setMessages(prev => [...prev, { 
        role: 'manee', 
        content: errorMessage 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendVoiceMessage(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info("🎤 Je t'écoute... Pose ta question !");
    } catch (err) {
      console.error("Microphone error:", err);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '🎤 (Message vocal...)' }]);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(`${API}/ploy/voice`, {
          session_id: sessionId,
          audio_base64: base64Audio,
          context: context || null
        });

        const transcription = response.data.transcription;
        const transcriptionDisplay = transcription 
          ? `🎤 "${transcription}"` 
          : '🎤 (Message vocal)';

        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = { role: 'user', content: transcriptionDisplay };
          return [...newMessages, { role: 'manee', content: response.data.response }];
        });
        
        setIsLoading(false);
      };
    } catch (err) {
      console.error('Voice message error:', err);
      setMessages(prev => [...prev, { 
        role: 'manee', 
        content: "Je n'ai pas bien compris ta question vocale 😅 Réessaie ou tape ton message !" 
      }]);
      setIsLoading(false);
    }
  };

  const playManeeResponse = async (text) => {
    try {
      setIsPlayingResponse(true);
      const response = await axios.post(
        `${API}/ploy/speak?text=${encodeURIComponent(text)}&speed=0.8`,
        null,
        { responseType: 'blob' }
      );
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsPlayingResponse(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      await audio.play();
    } catch (err) {
      console.error('Manee speak error:', err);
      setIsPlayingResponse(false);
    }
  };

  const resetConversation = async () => {
    try {
      await axios.delete(`${API}/ploy/session/${sessionId}`);
      localStorage.removeItem('ploy_session_id');
      setMessages([{
        role: 'manee',
        content: "Conversation réinitialisée ! 😊 On repart de zéro. Comment puis-je t'aider ?"
      }]);
      toast.success("Conversation réinitialisée !");
    } catch (err) {
      console.error('Reset error:', err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl bg-gradient-to-br from-pink-50 to-purple-50 border-none max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-lg">
              ม
            </div>
            <div>
              <span>Manee - Ta prof de thaï</span>
              <p className="text-xs font-normal text-slate-500">มณี ครูสอนภาษาไทย</p>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Chat interactif avec Manee, ta professeure virtuelle de thaï.
          </DialogDescription>
        </DialogHeader>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-[300px] max-h-[400px]">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-purple-500 text-white rounded-br-md'
                    : 'bg-white shadow-sm rounded-bl-md'
                }`}
              >
                {msg.role === 'ploy' && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-pink-600">Manee</span>
                    <button
                      onClick={() => playManeeResponse(msg.content)}
                      disabled={isPlayingResponse}
                      className="p-1 hover:bg-pink-100 rounded-full transition-colors"
                      title="Écouter Manee"
                    >
                      {isPlayingResponse ? (
                        <Loader2 className="w-3 h-3 animate-spin text-pink-500" />
                      ) : (
                        <Volume2 className="w-3 h-3 text-pink-500" />
                      )}
                    </button>
                  </div>
                )}
                <p className={`text-sm whitespace-pre-line ${msg.role === 'user' ? '' : 'text-slate-700'}`}>
                  {msg.content}
                </p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
                  <span className="text-sm text-slate-500">Manee réfléchit...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t border-purple-100 pt-4">
          <div className="flex gap-2">
            <button
              onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
              disabled={isLoading}
              className={`p-3 rounded-full transition-all ${
                isRecording
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
              title={isRecording ? "Arrêter" : "Parler à Manee"}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(inputText); }}
              className="flex-1 flex gap-2"
            >
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Pose ta question à Manee..."
                disabled={isLoading || isRecording}
                className="flex-1 rounded-full border-purple-200 focus:border-purple-400"
              />
              <Button
                type="submit"
                disabled={isLoading || !inputText.trim()}
                className="rounded-full bg-purple-500 hover:bg-purple-600"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>

            <button
              onClick={resetConversation}
              className="p-3 bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-colors"
              title="Nouvelle conversation"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-xs text-center text-slate-400 mt-2">
            💡 Tu peux me parler ou écrire en français. Je suis là pour t'aider !
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManeeChat;
